// Headless reads for the customer order detail page: the one status label the page and the Orders list agree
// on, which stop is being worked on, and the activity feed. Pure, so the page stays layout.
//
// "Delivered" is reserved for what says it arrived: the carrier's word from the parcel timeline (below), else
// Tharstern's own completed date (`delivery.delivered_on`). Every stop done and a quantity sent
// (`delivery.dispatched_on`) says the order has left us, which is "Despatched" (Brad, 8 Oct 2026): Tharstern posts its
// delivered quantity at despatch.

import type { CustomerStepperStop } from "../data/customer-api-types";
import type {
  CustomerOrderDetailDelivery,
  CustomerOrderDetailInvoice,
  CustomerOrderParcelEvents,
} from "../data/customer-api-types";

/** The stops a customer is shown, in order: a stop that does not apply to this job is not mentioned. */
export function visibleStops(
  stops: CustomerStepperStop[],
): CustomerStepperStop[] {
  return stops
    .filter((stop) => stop.state !== "skipped")
    .sort((a, b) => a.position - b.position);
}

export function stopLabel(stop: CustomerStepperStop): string {
  return stop.customer_display_name ?? stop.display_name;
}

/** Every stop has been reached: the last one is "Delivered" (or "Supplied from stock"). */
export function hasFinishedAllStops(stops: CustomerStepperStop[]): boolean {
  const shown = visibleStops(stops);
  return shown.length > 0 && shown.every((stop) => stop.state === "done");
}

type DeliveryEvidence = Pick<
  CustomerOrderDetailDelivery,
  "method" | "delivered_on" | "dispatched_on"
>;

export interface FinishedFacts {
  tone: "delivered" | "despatched";
  headline: string;
  dateLabel: string;
  date: string | null;
}

/**
 * What a job whose every stop is done may say about where the goods are, from the evidence the order read carries:
 * a carrier-confirmed delivery or Tharstern's completed date is "Delivered"; a quantity sent is "Despatched". With neither, null: the stops being
 * done says the work is finished, not that it has gone.
 *
 * A collection keeps "Collected" (dated by its completed date, else the last stop) whatever else is known. Whether
 * Tharstern's quantity on a collection is posted at handover or when the work is ready is not settled (doc 47), so its
 * wording is left as it was.
 */
export function finishedFacts(
  delivery: DeliveryEvidence | null | undefined,
  lastStopAt: string | null = null,
  carrier: CarrierProgress | null = null,
): FinishedFacts | null {
  // The carrier's word wins; Tharstern's own date, when it has one, is the date shown.
  if (carrier?.kind === "delivered") {
    return {
      tone: "delivered",
      headline: "Delivered",
      dateLabel: "Delivered on",
      date: delivery?.delivered_on ?? carrier.occurredAt,
    };
  }
  if (delivery?.method === "collection") {
    return {
      tone: "delivered",
      headline: "Collected",
      dateLabel: "Collected on",
      date: delivery.delivered_on ?? lastStopAt,
    };
  }
  if (delivery?.delivered_on) {
    return {
      tone: "delivered",
      headline: "Delivered",
      dateLabel: "Delivered on",
      date: delivery.delivered_on,
    };
  }
  if (delivery?.dispatched_on) {
    return {
      tone: "despatched",
      headline: "Despatched",
      dateLabel: "Despatched on",
      date: delivery.dispatched_on,
    };
  }
  return null;
}

export interface OrderActivityEvent {
  key: string;
  /** ISO timestamp the event happened at. */
  at: string;
  text: string;
  /** A quoted comment, when the event carried one. */
  detail?: string | null;
}

/**
 * Newest first: the stops reached and each invoice issued. The order's own
 * "Order placed" line is added only when there is no stepper to say it (a stop already reads "Ordered").
 */
export function buildOrderActivity(input: {
  stops: CustomerStepperStop[];
  invoices: CustomerOrderDetailInvoice[];
  orderedAt: string | null | undefined;
}): OrderActivityEvent[] {
  const events: OrderActivityEvent[] = [];
  const shownStops = visibleStops(input.stops);

  for (const stop of shownStops) {
    if (
      (stop.state === "done" || stop.state === "in_progress") &&
      stop.actual_at
    ) {
      events.push({
        key: `stop-${stop.stage_code}`,
        at: stop.actual_at,
        text: stopLabel(stop),
      });
    }
  }
  if (shownStops.length === 0 && input.orderedAt) {
    events.push({ key: "ordered", at: input.orderedAt, text: "Order placed" });
  }

  for (const invoice of input.invoices) {
    events.push({
      key: `invoice-${invoice.invoice_no}`,
      at: invoice.invoiced_on,
      text: `${invoice.is_credit_note ? "Credit note" : "Invoice"} ${invoice.invoice_no} issued`,
    });
  }

  return events.sort((a, b) => b.at.localeCompare(a.at));
}

// --- The carrier's word on where the parcel is ---------------------------------------------------------------------
//
// Our own milestones lag the carrier: Tharstern rarely records a delivery, and "On its way" stays current while the
// parcel is already on the van. When the parcel timeline's newest milestone says out for delivery or delivered, the
// page follows the carrier. Every sentence still comes from the timeline response (its headline, the newest event's
// label and time); this only decides which of the page's own stops they apply to.

export interface CarrierProgress {
  kind: "out_for_delivery" | "delivered";
  /** The timeline's own headline: "Out for delivery today with DPD", "Delivered today at 10:42". */
  headline: string;
  /** The newest milestone's own label ("Out for delivery"), for the stop it replaces. */
  label: string;
  occurredAt: string;
}

/** The carrier's progress, when the timeline answered with events and its newest milestone is out or arrived. */
export function carrierProgress(
  timeline: CustomerOrderParcelEvents | null | undefined,
): CarrierProgress | null {
  if (!timeline || timeline.state !== "events") return null;
  const newest = timeline.events[0];
  if (newest?.kind !== "out_for_delivery" && newest?.kind !== "delivered") {
    return null;
  }
  return {
    kind: newest.kind,
    headline: timeline.headline,
    label: newest.label,
    occurredAt: newest.occurred_at,
  };
}

/**
 * The job's stops as the carrier says they stand. Applies only once the job is on its last two stops (despatch,
 * delivery): a parcel that is out for a split delivery while the job is still in print says nothing of the job.
 * Out for delivery relabels the despatch stop in the carrier's own milestone wording; delivered completes the
 * rest, the last with Tharstern's delivered date when it has one (it wins) and the carrier's time when not.
 */
export function applyCarrierProgress(
  stops: CustomerStepperStop[],
  carrier: CarrierProgress | null,
  deliveredOn: string | null | undefined,
): CustomerStepperStop[] {
  if (!carrier) return stops;
  const shown = visibleStops(stops);
  const firstOpen = shown.findIndex((stop) => stop.state !== "done");
  if (firstOpen === -1 || firstOpen < shown.length - 2) return stops;
  const changed = new Map<string, CustomerStepperStop>();
  shown.forEach((stop, index) => {
    if (index < firstOpen) return;
    if (carrier.kind === "delivered") {
      const isLast = index === shown.length - 1;
      changed.set(stop.stage_code, {
        ...stop,
        state: "done",
        actual_at: isLast
          ? (deliveredOn ?? carrier.occurredAt)
          : (stop.actual_at ?? carrier.occurredAt),
      });
    } else if (index === shown.length - 2) {
      changed.set(stop.stage_code, {
        ...stop,
        state: "in_progress",
        customer_display_name: carrier.label,
      });
    }
  });
  return stops.map((stop) => changed.get(stop.stage_code) ?? stop);
}
